import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, query, orderBy, doc, getDoc, updateDoc,
  serverTimestamp, onSnapshot, where, getDocs
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError, showSuccess } from '../utils/notifications';

const ESTADOS = [
  { value: 'pendiente',    label: 'Pendiente',    color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-400' },
  { value: 'en_atencion',  label: 'En Atención',  color: 'bg-blue-100 text-blue-800',    dot: 'bg-blue-500'   },
  { value: 'resuelto',     label: 'Resuelto',     color: 'bg-green-100 text-green-800',  dot: 'bg-green-500'  },
  { value: 'cerrado',      label: 'Cerrado',      color: 'bg-gray-100 text-gray-600',    dot: 'bg-gray-400'   },
];

function estadoInfo(value) {
  return ESTADOS.find(e => e.value === value) || ESTADOS[0];
}

function BadgeEstado({ estado }) {
  const info = estadoInfo(estado);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${info.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${info.dot}`}></span>
      {info.label}
    </span>
  );
}

function formatFecha(ts) {
  if (!ts) return 'N/A';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return 'N/A'; }
}

export default function QuejasCiudadanas() {
  const navigate = useNavigate();
  const [user, setUser]           = useState(null);
  const [userRole, setUserRole]   = useState('');
  const [loading, setLoading]     = useState(true);
  const [reportes, setReportes]   = useState([]);
  const [filtro, setFiltro]       = useState('todos');
  const [busqueda, setBusqueda]   = useState('');
  const [selected, setSelected]   = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeSection]           = useState('quejas-ciudadanas');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Panel de gestión
  const [nuevoEstado, setNuevoEstado]     = useState('');
  const [comentario, setComentario]       = useState('');
  const [guardando, setGuardando]         = useState(false);
  const [fotoAmpliada, setFotoAmpliada]   = useState(false);

  // ── Listener en tiempo real ──────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'reportes_qr'), orderBy('fechaCreacion', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setReportes(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
    }, err => {
      console.error('Error leyendo reportes_qr:', err);
      showError('Error', 'No se pudieron cargar los reportes');
    });
    return () => unsub();
  }, []);

  // ── Auth ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async currentUser => {
      if (!currentUser) { navigate('/login'); return; }
      try {
        let userData = null;
        const snap = await getDoc(doc(db, 'usuarios', currentUser.email));
        if (snap.exists()) userData = snap.data();
        if (!userData) {
          const q = query(collection(db, 'usuarios'), where('email', '==', currentUser.email));
          const qs = await getDocs(q);
          if (!qs.empty) userData = qs.docs[0].data();
        }
        if (!userData || userData.rol !== 'Admin') {
          showError('Acceso denegado', 'Solo administradores');
          navigate('/dashboard');
          return;
        }
        setUser({ ...currentUser, nombre: userData.nombre });
        setUserRole(userData.rol);
      } catch (e) {
        console.error(e);
        navigate('/login');
      }
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  // ── Filtros ──────────────────────────────────────────────────────────────
  const reportesFiltrados = reportes.filter(r => {
    const matchEstado = filtro === 'todos' || r.estado === filtro;
    const q = busqueda.toLowerCase();
    const matchBusqueda = !q ||
      (r.numero      || '').toLowerCase().includes(q) ||
      (r.direccion   || '').toLowerCase().includes(q) ||
      (r.email       || '').toLowerCase().includes(q) ||
      (r.detalleSuceso || '').toLowerCase().includes(q) ||
      (r.telefonoContacto || '').toLowerCase().includes(q);
    return matchEstado && matchBusqueda;
  });

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:       reportes.length,
    pendiente:   reportes.filter(r => r.estado === 'pendiente').length,
    en_atencion: reportes.filter(r => r.estado === 'en_atencion').length,
    resuelto:    reportes.filter(r => r.estado === 'resuelto').length,
    cerrado:     reportes.filter(r => r.estado === 'cerrado').length,
  };

  // ── Abrir panel ──────────────────────────────────────────────────────────
  const abrirPanel = useCallback((reporte) => {
    setSelected(reporte);
    setNuevoEstado(reporte.estado || 'pendiente');
    setComentario('');
    setFotoAmpliada(false);
    setPanelOpen(true);
  }, []);

  const cerrarPanel = () => { setPanelOpen(false); setSelected(null); };

  // ── Guardar gestión ──────────────────────────────────────────────────────
  const guardarGestion = async () => {
    if (!selected) return;
    if (!comentario.trim()) { showError('Requerido', 'Escribe un comentario para registrar la gestión'); return; }
    setGuardando(true);
    try {
      const ref = doc(db, 'reportes_qr', selected.docId);
      const entrada = {
        fecha:          new Date().toISOString(),
        estadoAnterior: selected.estado || 'pendiente',
        estadoNuevo:    nuevoEstado,
        comentario:     comentario.trim(),
        gestionadoPor:  user?.email || 'Admin',
        nombreAdmin:    user?.nombre || user?.email || 'Admin',
      };
      const historial = Array.isArray(selected.historialGestion) ? [...selected.historialGestion, entrada] : [entrada];
      await updateDoc(ref, {
        estado:           nuevoEstado,
        respuesta:        comentario.trim(),
        gestionadoPor:    user?.email || 'Admin',
        nombreAdmin:      user?.nombre || user?.email || 'Admin',
        historialGestion: historial,
        fechaUltimaGestion: serverTimestamp(),
      });
      setSelected(prev => ({ ...prev, estado: nuevoEstado, respuesta: comentario.trim(), historialGestion: historial }));
      setComentario('');
      showSuccess('Guardado', 'Gestión registrada correctamente');
    } catch (e) {
      console.error(e);
      showError('Error', 'No se pudo guardar la gestión');
    }
    setGuardando(false);
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <MobileHeader title="Reportes Ciudadanos" userEmail={user?.email} userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
      <div className="md:hidden h-14"></div>

      <div className="flex flex-1 overflow-hidden">
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={() => setIsMobileMenuOpen(false)} />
        )}
        <Sidebar activeSection={activeSection} setActiveSection={() => {}}
          isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />

        {/* ── CONTENIDO PRINCIPAL ── */}
        <div className="flex-1 overflow-auto">
          {/* Header desktop */}
          <header className="bg-white shadow px-6 py-4 hidden md:flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Reportes Ciudadanos</h1>
              <p className="text-sm text-gray-500 mt-0.5">Gestión de reportes recibidos por QR y web</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{user?.email}</span>
              <button onClick={async () => { await auth.signOut(); navigate('/login'); }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded">
                Cerrar Sesión
              </button>
            </div>
          </header>

          <main className="p-6">
            {/* ── STATS ── */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Total',       value: stats.total,       bg: 'bg-white',          text: 'text-gray-800',  border: 'border-gray-200' },
                { label: 'Pendientes',  value: stats.pendiente,   bg: 'bg-yellow-50',      text: 'text-yellow-700',border: 'border-yellow-200' },
                { label: 'En Atención', value: stats.en_atencion, bg: 'bg-blue-50',        text: 'text-blue-700',  border: 'border-blue-200' },
                { label: 'Resueltos',   value: stats.resuelto,    bg: 'bg-green-50',       text: 'text-green-700', border: 'border-green-200' },
                { label: 'Cerrados',    value: stats.cerrado,     bg: 'bg-gray-50',        text: 'text-gray-600',  border: 'border-gray-200' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4 text-center shadow-sm`}>
                  <p className={`text-3xl font-bold ${s.text}`}>{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* ── FILTROS Y BÚSQUEDA ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {[{ value: 'todos', label: 'Todos' }, ...ESTADOS].map(e => (
                  <button key={e.value}
                    onClick={() => setFiltro(e.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filtro === e.value
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {e.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-72">
                <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" placeholder="Buscar por ID, dirección, email..."
                  value={busqueda} onChange={e => setBusqueda(e.target.value)}
                  className="pl-9 w-full text-sm border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* ── TABLA ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID / Fecha', 'Dirección', 'Detalle', 'Contacto', 'Origen', 'Estado', 'Gestor', 'Acción'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {reportesFiltrados.length === 0 && (
                      <tr><td colSpan="8" className="px-4 py-12 text-center text-gray-400">No hay reportes que coincidan con el filtro.</td></tr>
                    )}
                    {reportesFiltrados.map(r => (
                      <tr key={r.docId} className="hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => abrirPanel(r)}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="text-sm font-semibold text-blue-700">{r.numero || r.id || r.docId}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{formatFecha(r.fechaCreacion)}</p>
                        </td>
                        <td className="px-4 py-3 max-w-[160px]">
                          <p className="text-sm text-gray-700 truncate">{r.direccion || 'N/A'}</p>
                        </td>
                        <td className="px-4 py-3 max-w-[200px]">
                          <p className="text-sm text-gray-600 truncate">{r.detalleSuceso || '—'}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="text-xs text-gray-700">{r.email || '—'}</p>
                          <p className="text-xs text-gray-400">{r.telefonoContacto || '—'}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {r.origen || 'Web'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <BadgeEstado estado={r.estado} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="text-xs text-gray-500 truncate max-w-[120px]">{r.gestionadoPor || <span className="italic text-gray-300">Sin asignar</span>}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button onClick={e => { e.stopPropagation(); abrirPanel(r); }}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 font-medium">
                            Gestionar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-3 text-right">
              {reportesFiltrados.length} de {reportes.length} reportes
            </p>
          </main>
        </div>

        {/* ── PANEL DE DETALLE / GESTIÓN ── */}
        {panelOpen && selected && (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black bg-opacity-40 z-30" onClick={cerrarPanel} />

            {/* Panel deslizante derecho */}
            <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-40 flex flex-col overflow-hidden">
              {/* Cabecera del panel */}
              <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-900">{selected.numero || selected.id || selected.docId}</h2>
                    <BadgeEstado estado={selected.estado} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Recibido: {formatFecha(selected.fechaCreacion)} · Origen: <span className="font-medium text-purple-600">{selected.origen || 'Web'}</span>
                  </p>
                </div>
                <button onClick={cerrarPanel} className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 transition-colors">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Cuerpo scrollable */}
              <div className="flex-1 overflow-y-auto">
                {/* Sección: Información del reporte */}
                <section className="px-6 py-5 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Información del Reporte</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField label="Dirección" value={selected.direccion} />
                    <InfoField label="Teléfono" value={selected.telefonoContacto} />
                    <InfoField label="Email" value={selected.email} />
                    <InfoField label="IP" value={selected.ip} />
                    <InfoField label="Fecha legible" value={selected.fechaLegible} />
                    <InfoField label="User Agent" value={selected.userAgent} small />
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Detalle del suceso</p>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 leading-relaxed border border-gray-200">
                      {selected.detalleSuceso || <span className="italic text-gray-400">Sin detalle</span>}
                    </div>
                  </div>
                </section>

                {/* Sección: Foto */}
                {selected.fotoUrl && (
                  <section className="px-6 py-5 border-b border-gray-100">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Fotografía Adjunta</h3>
                    <div className="relative">
                      <img
                        src={selected.fotoUrl}
                        alt="Foto del reporte"
                        className={`rounded-lg border border-gray-200 cursor-pointer transition-all ${fotoAmpliada ? 'w-full' : 'h-48 object-cover'}`}
                        onClick={() => setFotoAmpliada(!fotoAmpliada)}
                      />
                      <button onClick={() => setFotoAmpliada(!fotoAmpliada)}
                        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        {fotoAmpliada ? 'Reducir' : 'Ampliar'}
                      </button>
                    </div>
                  </section>
                )}
                {!selected.fotoUrl && (
                  <section className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Fotografía Adjunta</h3>
                    <p className="text-sm text-gray-400 italic">No se adjuntó fotografía</p>
                  </section>
                )}

                {/* Sección: Historial de gestión */}
                <section className="px-6 py-5 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Historial de Gestión</h3>
                  {(!selected.historialGestion || selected.historialGestion.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">Sin acciones registradas aún.</p>
                  ) : (
                    <div className="relative">
                      {/* Línea vertical */}
                      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      <div className="space-y-4 pl-8">
                        {[...selected.historialGestion].reverse().map((h, i) => {
                          const desde = estadoInfo(h.estadoAnterior);
                          const hasta = estadoInfo(h.estadoNuevo);
                          return (
                            <div key={i} className="relative">
                              <div className={`absolute -left-5 w-3 h-3 rounded-full border-2 border-white ${hasta.dot} shadow`}></div>
                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${desde.color}`}>{desde.label}</span>
                                    <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${hasta.color}`}>{hasta.label}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    {h.fecha ? new Date(h.fecha).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1">{h.comentario}</p>
                                <p className="text-xs text-gray-400 mt-1">Por: <span className="font-medium text-gray-600">{h.nombreAdmin || h.gestionadoPor}</span></p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>

                {/* Sección: Nueva gestión */}
                <section className="px-6 py-5">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Registrar Gestión</h3>

                  {/* Selector de estado */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cambiar Estado</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {ESTADOS.map(e => (
                        <button key={e.value} onClick={() => setNuevoEstado(e.value)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                            nuevoEstado === e.value
                              ? `${e.color} border-current shadow-sm`
                              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                          }`}>
                          {e.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Comentario */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentario / Acción tomada <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={comentario}
                      onChange={e => setComentario(e.target.value)}
                      rows={4}
                      placeholder="Describe la acción tomada, respuesta al ciudadano, o nota interna..."
                      className="w-full text-sm border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Info del admin actual */}
                  <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                    <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Se registrará como: <span className="font-semibold text-blue-700">{user?.nombre || user?.email}</span>
                  </div>

                  <button onClick={guardarGestion} disabled={guardando || !comentario.trim()}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                      guardando || !comentario.trim()
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    }`}>
                    {guardando ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Guardando...
                      </span>
                    ) : 'Guardar Gestión'}
                  </button>
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value, small }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 mb-0.5">{label}</p>
      <p className={`${small ? 'text-xs' : 'text-sm'} text-gray-700 break-all`}>
        {value || <span className="italic text-gray-300">N/A</span>}
      </p>
    </div>
  );
}
